/**
 * The FAQ section templates, ported from faqs.me (`src/templates/defs/*.ts`).
 *
 * Each module exports one template: metadata for the dashboard's gallery and
 * a `render(ctx)` that returns `{ html, css, cssDark? }`. The contract is
 * faqs.me's own (see `slate.js`, the reference implementation), with three
 * additions the engine provides: `ctx.t(key)` for the fixed copy, `searchText`
 * on each item for the search templates, and `ctx.contactHref` /
 * `ctx.contactCta` / `ctx.hostLabel` for Beacon's card and Terminal's prompt.
 *
 * Adding a template from faqs.me: transpile its def to JS, drop the paywall
 * teaser branches, route any fixed copy through `ctx.t()`, register it here,
 * and add its id to `App\Services\FaqSections\FaqSectionDesign::TEMPLATES`
 * (tests/Unit/FaqSectionTemplateRegistryTest.php holds the two lists together).
 */

import slate from './slate';
import glass from './glass';
import bubble from './bubble';
import ledger from './ledger';
import duet from './duet';
import pivot from './pivot';
import atlas from './atlas';
import haiku from './haiku';
import trail from './trail';
import beacon from './beacon';
import blok from './blok';
import prism from './prism';
import terminal from './terminal';
import marquee from './marquee';
import orbit from './orbit';
import mosaic from './mosaic';
import ticket from './ticket';
import finder from './finder';
import folio from './folio';
import neon from './neon';
import rail from './rail';
import weave from './weave';
import cove from './cove';
import postcard from './postcard';
import bulletin from './bulletin';
import dialogue from './dialogue';
import canopy from './canopy';

export const TEMPLATES = [
    slate, glass, bubble, ledger, duet, pivot, atlas, haiku, trail, beacon, blok,
    prism, terminal, marquee, orbit, mosaic, ticket, finder, folio, neon, rail, weave,
    cove, postcard, bulletin, dialogue, canopy,
];

/** What a section created in the dashboard starts with. */
export const DEFAULT_TEMPLATE_ID = 'slate';

/**
 * Not a template: a section that never picked one keeps the renderer it was
 * built with (site/embeddables/FAQSection.jsx), and every section saved before
 * templates existed is one of those.
 */
export const CLASSIC_TEMPLATE_ID = 'classic';

const TEMPLATE_MAP = Object.fromEntries(TEMPLATES.map((template) => [template.id, template]));

export function hasTemplate(id) {
    return Object.prototype.hasOwnProperty.call(TEMPLATE_MAP, id);
}

/**
 * The template for an id, or the default one for an id this bundle does not
 * know — a page can hold a cached copy of the embed bundle older than the
 * section it renders.
 */
export function getTemplate(id) {
    return TEMPLATE_MAP[id] || TEMPLATE_MAP[DEFAULT_TEMPLATE_ID];
}
